import { useDispatch } from "react-redux";
import { setIsEditingNotesForProposal, setNoteForProposalAct } from "../../../../../../features/actuarial/remark-slice";
import { EditIconSVG } from "../../../../../../assets/icon/svg";
import RichTextViewer from "../../../../../../library/ckeditor/rich-text-viewer";
import { useComputationActions } from "../../../../../../hooks/actions/user-computation-actions";
import CKEditor from "../../../../../../library/ckeditor/ck-editor";
import { useProposalComputation } from "../../../../../../hooks/proposal/use-proposal-computation";

export default function ActNotesForPdf() {

    const dispatch = useDispatch();
    const {
        actNotePDF,
        isShowActNotePDF,
        isEditingNotesForProposal,
        noteForProposalActData,
    } = useProposalComputation()

    const {
        handleSaveNotesForProposal,
        handleCancelSaveNotesForProposal,
        isLoadingSaveNotes,
        cantEdit,
    } = useComputationActions();

    const handleEdit = () => {
        dispatch(setNoteForProposalAct({
            isShow: isShowActNotePDF,
            notesProposal: actNotePDF,
            showCKEdit: false,
            copyNotes: "",
        }));

        dispatch(setIsEditingNotesForProposal(true));
    };

    const handleCancelEdit = () => {
        dispatch(setIsEditingNotesForProposal(false));
        handleCancelSaveNotesForProposal("proposal");
        dispatch(setNoteForProposalAct({
            isShow: false,
            notesProposal: "",
            showCKEdit: false,
            copyNotes: "",
        }));
    };

    const handleSave = async () => {
        await handleSaveNotesForProposal(
            noteForProposalActData.notesProposal,
            noteForProposalActData.isShow
        );
    };

    const handleClickCKEditor = () => {
        dispatch(setNoteForProposalAct({
            showCKEdit: true,
            copyNotes: noteForProposalActData.notesProposal,
        }));
    };

    const handleCancelCKEditor = () => {
        dispatch(setNoteForProposalAct({
            showCKEdit: false,
            copyNotes: "",
        }));
    };

    const handleSaveCKEditor = () => {
        dispatch(setNoteForProposalAct({
            notesProposal: noteForProposalActData.copyNotes,
            showCKEdit: false,
            copyNotes: "",
        }));
    };

    const isShowToggle = isEditingNotesForProposal
        ? noteForProposalActData.isShow
        : isShowActNotePDF;

    const isSaveDisabled = cantEdit || isLoadingSaveNotes;

    return (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-3 py-3 sm:px-4">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <span className="h-2 w-2 shrink-0 rounded-full bg-[#186cb2]" />
                        <h3 className="truncate text-xs font-semibold text-gray-800">Notes for Proposal</h3>
                    </div>
                    <p className="mt-0.5 text-[10px] leading-4 text-gray-500 sm:text-[11px]">
                        Enter notes that should appear in the proposal document for the client.
                    </p>
                </div>

                {!isEditingNotesForProposal ? (
                    <button
                        type="button"
                        aria-label="Edit notes"
                        title="Edit notes"
                        disabled={cantEdit || isLoadingSaveNotes}
                        onClick={handleEdit}
                        className="flex size-9 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-[#186cb2] transition-colors hover:bg-[#186cb2]/5 disabled:cursor-not-allowed disabled:opacity-50 sm:h-auto sm:w-auto sm:gap-1.5 sm:px-3 sm:py-1.5"
                    >
                        <EditIconSVG size={14} />
                        <span className="hidden sm:inline text-xs font-medium">Edit</span>
                    </button>
                ) : (
                    <div className="flex shrink-0 items-center gap-2">
                        <button
                            type="button"
                            aria-label="Cancel"
                            title="Cancel"
                            disabled={isLoadingSaveNotes}
                            onClick={handleCancelEdit}
                            className="flex size-9 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:h-auto sm:w-auto sm:gap-1.5 sm:px-3 sm:py-1.5"
                        >
                            <span className="text-base leading-none">×</span>
                            <span className="hidden text-xs font-medium sm:inline">Cancel</span>
                        </button>

                        <button
                            type="button"
                            aria-label={isLoadingSaveNotes ? "Saving notes" : "Save notes"}
                            title={isLoadingSaveNotes ? "Saving..." : "Save"}
                            disabled={isSaveDisabled}
                            onClick={handleSave}
                            className="flex size-9 items-center justify-center rounded-md border border-[#186cb2] bg-[#186cb2] text-white transition-colors hover:bg-[#155b96] disabled:cursor-not-allowed disabled:opacity-50 sm:h-auto sm:w-auto sm:gap-1.5 sm:px-3 sm:py-1.5"
                        >
                            {isLoadingSaveNotes ? (
                                <span className="size-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                            ) : (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-3.5">
                                    <path d="M20 6 9 17l-5-5" />
                                </svg>
                            )}
                            <span className="hidden text-xs font-medium sm:inline">
                                {isLoadingSaveNotes ? "Saving..." : "Save"}
                            </span>
                        </button>
                    </div>
                )}
            </div>
            {/* Settings */}
            <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-white px-3 py-3 sm:px-4">
                <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-gray-700">Display in Proposal</p>
                    <p className="mt-0.5 text-[10px] leading-4 text-gray-400 sm:text-[11px]">
                        Choose whether these notes should appear in the proposal document.
                    </p>
                </div>

                <div className={`flex shrink-0 items-center gap-2 rounded-md border px-2 py-1.5 sm:gap-2.5 sm:px-2.5 ${isShowToggle ? "border-[#186cb2]/20 bg-[#eef6fc]" : "border-gray-200 bg-gray-50"}`}>
                    <span className={`text-[10px] font-semibold sm:text-[11px] ${isShowToggle ? "text-[#186cb2]" : "text-gray-500"}`}>
                        {isShowToggle ? "Visible" : "Hidden"}
                    </span>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={isShowToggle}
                        aria-label="Show notes in proposal"
                        disabled={!isEditingNotesForProposal}
                        onClick={() => dispatch(setNoteForProposalAct({ isShow: !isShowToggle }))}
                        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${isShowToggle ? "bg-[#186cb2]" : "bg-gray-300"} disabled:cursor-not-allowed disabled:opacity-50`}
                    >
                        <span className={`h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform ${isShowToggle ? "translate-x-[18px]" : "translate-x-[3px]"}`} />
                    </button>
                </div>
            </div>

            {/* Notes Preview */}
            <div className="p-4">
                <div className="mb-2 flex items-center justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                        Preview
                    </span>
                    {isEditingNotesForProposal && (
                        <button
                            type="button"
                            onClick={handleClickCKEditor}
                            className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-[#186cb2] transition-colors hover:bg-[#eef6fc] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <EditIconSVG size={12} />
                            <span>Edit Notes</span>
                        </button>
                    )}
                </div>
                <div className="rounded-md border border-gray-100 bg-gray-50/50 p-3">
                    <RichTextViewer
                        value={isEditingNotesForProposal
                            ? noteForProposalActData.notesProposal
                            : actNotePDF}
                    />
                </div>
            </div>
            {/* CKEditor */}
            <CKEditor
                isOpen={noteForProposalActData.showCKEdit}
                title="Notes for Proposal"
                value={noteForProposalActData.showCKEdit
                    ? noteForProposalActData.copyNotes
                    : noteForProposalActData.notesProposal}
                onChange={(e) => dispatch(setNoteForProposalAct({ copyNotes: e }))}
                onClose={handleCancelCKEditor}
                onSave={handleSaveCKEditor}
                isLoading={false}
            />
        </div>
    );
}